import { useEffect, useMemo, useState } from "react";
import Navbar from "../components/layout/Navbar";
import Loader from "../components/ui/Loader";
import useFinancialReportData from "../hooks/useFinancialReportData";
import { formatCurrencyValue } from "../services/exportTratamenteService";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { useUsers } from "../context/UsersContext";

const formatCurrency = (value) =>
  formatCurrencyValue(Number.isFinite(Number(value)) ? Number(value) : 0);

const formatDateValue = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("ro-RO", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const normalizeEmail = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const normalizeId = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

const isPlaceholderName = (value) => {
  if (typeof value !== "string") return true;
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (trimmed.includes("@")) return true;
  if (/^[0-9a-f]{24}$/i.test(trimmed)) return true;
  return false;
};

const pickDisplayName = (values, fallback) => {
  for (const value of values) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed || isPlaceholderName(trimmed)) continue;
    return trimmed;
  }
  return fallback;
};

export default function FinancialReport() {
  const { loading, error, monthOrder, monthlyData } = useFinancialReportData();
  const { users = [] } = useUsers() || {};
  const [expandedMedic, setExpandedMedic] = useState({ revenues: new Map(), commissions: new Map() });

  const toggleMedicSection = (type, key) => {
    setExpandedMedic((prev) => {
      const previous = prev[type] instanceof Map ? prev[type] : new Map();
      const next = new Map(previous);
      next.set(key, !next.get(key));
      return {
        ...prev,
        [type]: next,
      };
    });
  };

  const isSectionOpen = (type, key) => {
    const bucket = expandedMedic[type];
    return bucket instanceof Map ? bucket.get(key) ?? false : false;
  };

  const [activeTab, setActiveTab] = useState("revenues");
  const [selectedMonthKey, setSelectedMonthKey] = useState(null);
  const [selectedChartMonthKey, setSelectedChartMonthKey] = useState(null);

  useEffect(() => {
    if (!monthOrder.length) return;

    setSelectedMonthKey((prev) =>
      prev && monthOrder.includes(prev) ? prev : monthOrder[0]
    );
    setSelectedChartMonthKey((prev) =>
      prev && monthOrder.includes(prev) ? prev : monthOrder[0]
    );
  }, [monthOrder]);

  useEffect(() => {
    setExpandedMedic({ revenues: new Map(), commissions: new Map() });
  }, [selectedMonthKey]);

  const medicLookup = useMemo(() => {
    const map = new Map();
    users.forEach((user) => {
      const emailKey = normalizeEmail(user.email);
      if (emailKey) {
        map.set(`email:${emailKey}`, user);
      }
      const idKey =
        typeof user.id === "string" && user.id
          ? normalizeId(user.id)
          : "";
      if (idKey) {
        map.set(`id:${idKey}`, user);
      }
    });
    return map;
  }, [users]);

  const enrichedMonthlyData = useMemo(() => {
    const enhanceMedic = (medic) => {
      const raw = medic && typeof medic === "object" ? medic : {};
      const email = raw.email || "";
      const id =
        typeof raw.id === "string"
          ? raw.id
          : typeof raw.medicId === "string"
            ? raw.medicId
            : "";
      const normalizedEmail = normalizeEmail(
        raw.normalizedEmail || email
      );
      const normalizedId = normalizeId(raw.normalizedId || id);

      let user = null;
      if (normalizedEmail && medicLookup.has(`email:${normalizedEmail}`)) {
        user = medicLookup.get(`email:${normalizedEmail}`);
      } else if (normalizedId && medicLookup.has(`id:${normalizedId}`)) {
        user = medicLookup.get(`id:${normalizedId}`);
      }

      const fallback = email || id || "Medic necunoscut";
      const name = pickDisplayName(
        [
          user?.name,
          raw.name,
          raw.fullName,
          raw.displayName,
          fallback,
        ],
        fallback
      );

      return {
        ...raw,
        id: user?.id || id,
        email: user?.email || email,
        name,
      };
    };

    const enrichCollection = (items = []) =>
      Array.isArray(items)
        ? items.map((item) => ({
            ...item,
            medic: enhanceMedic(item?.medic),
          }))
        : [];

    const result = {};
    Object.entries(monthlyData || {}).forEach(([key, value]) => {
      if (!value || typeof value !== "object") return;
      result[key] = {
        ...value,
        revenues: enrichCollection(value.revenues),
        commissions: enrichCollection(value.commissions),
      };
    });
    return result;
  }, [monthlyData, medicLookup]);

  const currentMonthData = useMemo(() => {
    if (!selectedMonthKey || !enrichedMonthlyData[selectedMonthKey]) {
      return {
        label: "",
        revenues: [],
        totalRevenue: 0,
        commissions: [],
        totalCommission: 0,
      };
    }
    return enrichedMonthlyData[selectedMonthKey];
  }, [selectedMonthKey, enrichedMonthlyData]);

  const archiveData = useMemo(() => {
    return monthOrder
      .filter((key) => key !== selectedMonthKey)
      .map((key) => ({
        monthKey: key,
        ...enrichedMonthlyData[key],
      }));
  }, [monthOrder, enrichedMonthlyData, selectedMonthKey]);

  const renderMonthSelect = (value, onChange) => (
    monthOrder.length ? (
      <select
        className="border border-blue-200 rounded-lg bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 w-full sm:w-auto"
        value={value || monthOrder[0]}
        onChange={(e) => onChange(e.target.value)}
      >
        {monthOrder.map((key) => (
          <option key={key} value={key}>
            {monthlyData[key]?.label || key}
          </option>
        ))}
      </select>
    ) : (
      <select className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500 w-full sm:w-auto" disabled>
        <option>Fără date</option>
      </select>
    )
  );

  const renderRevenuesTab = () => {
    if (!monthOrder.length || !selectedMonthKey) {
      return (
        <p className="text-sm text-gray-600">
          Nu există încă înregistrări financiare pentru a genera raportul.
        </p>
      );
    }

    const headerLabel = `Încăsări lună ${currentMonthData.label || "-"}`;

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50/50 px-4 py-3 sm:px-6 sm:py-4 shadow-sm">
          <h2 className="text-sm sm:text-base font-semibold text-gray-800">
            {headerLabel}: {" "}
            <span className="text-blue-700">
              {formatCurrency(currentMonthData.totalRevenue)} RON
            </span>
          </h2>
          <div className="w-full sm:w-auto">
            {renderMonthSelect(selectedMonthKey, setSelectedMonthKey)}
          </div>
        </div>

        <div className="space-y-3">
          {currentMonthData.revenues.length === 0 ? (
            <p className="text-sm text-gray-600">
              Nu există încasări pentru luna selectată.
            </p>
          ) : (
            currentMonthData.revenues.map((item, index) => {
              const medicKey = `${item.medic.email || item.medic.id || item.medic.name || "medic"}-${index}`;
              const isOpen = isSectionOpen("revenues", medicKey);

              return (
                <div
                  key={medicKey}
                  className="border border-gray-200 rounded-2xl bg-white shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => toggleMedicSection("revenues", medicKey)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-blue-50 transition rounded-2xl rounded-b-none"
                  >
                    <div>
                      <p className="text-sm sm:text-base font-semibold text-gray-800">
                        {item.medic.name}
                      </p>
                      <p className="text-xs text-gray-500">{item.medic.email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-sm font-semibold text-blue-700 whitespace-nowrap">
                        Total: {formatCurrency(item.total)} RON
                      </p>
                      <span
                        className={`text-lg leading-none text-blue-700 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      >
                        ▾
                      </span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="overflow-x-auto border-t border-gray-100">
                      <table className="w-full text-xs sm:text-sm">
                        <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide text-left">
                          <tr>
                            <th className="px-3 py-2">Data</th>
                            <th className="px-3 py-2">Pacient</th>
                            <th className="px-3 py-2 text-right">Sumă încasată</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {item.entries.map((entry, idx) => (
                            <tr
                              key={`${medicKey}-${entry.patientId}-${idx}`}
                              className="text-gray-700"
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                                {formatDateValue(entry.date)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap font-medium">
                                {entry.patientName}
                              </td>
                              <td className="px-3 py-2 text-right font-semibold text-blue-700 whitespace-nowrap">
                                {formatCurrency(entry.amount)} RON
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderCommissionsTab = () => {
    if (!monthOrder.length || !selectedMonthKey) {
      return (
        <p className="text-sm text-gray-600">
          Nu există comisioane calculate încă.
        </p>
      );
    }

    const headerLabel = `Comisioane lună ${currentMonthData.label || "-"}`;

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50/50 px-4 py-3 sm:px-6 sm:py-4 shadow-sm">
          <h2 className="text-sm sm:text-base font-semibold text-gray-800">
            {headerLabel}: {" "}
            <span className="text-blue-700">
              {formatCurrency(currentMonthData.totalCommission)} RON
            </span>
          </h2>
          <div className="w-full sm:w-auto">
            {renderMonthSelect(selectedMonthKey, setSelectedMonthKey)}
          </div>
        </div>

        <div className="space-y-3">
          {currentMonthData.commissions.length === 0 ? (
            <p className="text-sm text-gray-600">
              Nu există comisioane înregistrate pentru luna selectată.
            </p>
          ) : (
            currentMonthData.commissions.map((item, index) => {
              const medicKey = `${item.medic.email || item.medic.id || item.medic.name || "medic"}-${index}`;
              const isOpen = isSectionOpen("commissions", medicKey);

              return (
                <div
                  key={medicKey}
                  className="border border-gray-200 rounded-2xl bg-white shadow-sm"
                >
                  <button
                    type="button"
                    onClick={() => toggleMedicSection("commissions", medicKey)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-blue-50 transition rounded-2xl rounded-b-none"
                  >
                    <div>
                      <p className="text-sm sm:text-base font-semibold text-gray-800">
                        {item.medic.name}
                      </p>
                      <p className="text-xs text-gray-500">{item.medic.email}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-sm font-semibold text-blue-700 whitespace-nowrap">
                        Total: {formatCurrency(item.total)} RON
                      </p>
                      <span
                        className={`text-lg leading-none text-blue-700 transition-transform ${isOpen ? "rotate-180" : ""}`}
                      >
                        ▾
                      </span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="overflow-x-auto border-t border-gray-100">
                      <table className="w-full text-xs sm:text-sm">
                        <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide text-left">
                          <tr>
                            <th className="px-3 py-2">Data</th>
                            <th className="px-3 py-2">Pacient</th>
                            <th className="px-3 py-2">Procent</th>
                            <th className="px-3 py-2 text-right">Comision</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {item.entries.map((entry, idx) => (
                            <tr
                              key={`${medicKey}-${entry.patientId}-${idx}`}
                              className="text-gray-700"
                            >
                              <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                                {formatDateValue(entry.date)}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap font-medium">
                                {entry.patientName}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                                {entry.category === "retratamente" ? "10%" : "20%"}
                              </td>
                              <td className="px-3 py-2 text-right font-semibold text-blue-700 whitespace-nowrap">
                                {formatCurrency(entry.amount)} RON
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  };

  const renderChartTab = () => {
    const chartData = [...monthOrder]
      .reverse()
      .map((key) => ({
        key,
        name: enrichedMonthlyData[key]?.label || key,
        total: enrichedMonthlyData[key]?.totalRevenue || 0,
      }));

    if (!chartData.length) {
      return (
        <p className="text-sm text-gray-600">
          Nu există încăsări suficiente pentru a genera graficul.
        </p>
      );
    }

    const availableKeys = chartData.map((item) => item.key);
    const fallbackKey = availableKeys[availableKeys.length - 1];
    const activeChartKey =
      availableKeys.includes(selectedChartMonthKey)
        ? selectedChartMonthKey
        : fallbackKey;

    const chartLabel = enrichedMonthlyData[activeChartKey]?.label || "";

    const selectedSummary =
      enrichedMonthlyData[activeChartKey]?.summary || {
        newPatients: 0,
        totalPatients: 0,
        newDoctors: 0,
        totalDoctors: 0,
      };

    const totalSelectedRevenue =
      enrichedMonthlyData[activeChartKey]?.totalRevenue ?? 0;

    const CustomTooltip = ({ active, payload, label } = {}) => {
      const safePayload = Array.isArray(payload) ? payload : [];
      if (active && safePayload.length) {
        const value = safePayload[0]?.value ?? 0;
        return (
          <div className="rounded-xl border border-blue-100 bg-white px-3 py-2 shadow-lg text-xs sm:text-sm">
            <p className="font-semibold text-gray-800">{label}</p>
            <p className="text-blue-700 font-semibold">
              {formatCurrency(value)} RON
            </p>
          </div>
        );
      }
      return null;
    };

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50/50 px-4 py-3 sm:px-6 sm:py-4 shadow-sm">
          <div>
            <h2 className="text-sm sm:text-base font-semibold text-gray-800">
              Analiză lunară – {chartLabel || "Lună curentă"}
            </h2>
            <p className="text-xs text-gray-600">
              Încăsări luna selectată: {" "}
              <span className="text-blue-700 font-semibold">
                {formatCurrency(totalSelectedRevenue)} RON
              </span>
            </p>
          </div>
          <div className="w-full sm:w-auto">
            {renderMonthSelect(selectedChartMonthKey, setSelectedChartMonthKey)}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-blue-100 shadow-lg p-4 sm:p-6">
          <div className="h-72 sm:h-[360px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <defs>
                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.6} />
                  </linearGradient>
                  <linearGradient id="revenueGradientActive" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1d4ed8" stopOpacity={1} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0.75} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                <YAxis
                  tick={{ fontSize: 11 }}
                  stroke="#9ca3af"
                  tickFormatter={(value) => `${Math.round(value / 100) / 10}k`}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(59,130,246,0.08)" }} />
                <Bar dataKey="total" radius={[12, 12, 0, 0]}>
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.key}
                      fill={entry.key === activeChartKey ? "url(#revenueGradientActive)" : "url(#revenueGradient)"}
                      onClick={() => setSelectedChartMonthKey(entry.key)}
                      style={{ cursor: "pointer" }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            {
              label: "Pacienți noi",
              value: selectedSummary.newPatients,
            },
            {
              label: "Pacienți total",
              value:
                selectedSummary.totalPatientsOverall ??
                selectedSummary.totalPatients,
            },
            {
              label: "Medici noi",
              value: selectedSummary.newDoctors,
            },
            {
              label: "Medici total",
              value:
                selectedSummary.totalDoctorsOverall ??
                selectedSummary.totalDoctors,
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-2xl border border-blue-100 bg-blue-50/40 px-3 py-3 text-center shadow-sm"
            >
              <p className="text-[11px] uppercase tracking-wide text-blue-600">{stat.label}</p>
              <p className="text-xl font-semibold text-blue-900">
                {Number(stat.value || 0).toLocaleString("ro-RO")}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderArchiveTab = () => {
    if (!archiveData.length) {
      return (
        <p className="text-sm text-gray-600">
          Nu există înregistrări arhivate încă.
        </p>
      );
    }

    return (
      <div className="space-y-6">
        {archiveData.map((archive) => (
          <div key={archive.monthKey} className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                {archive.label}
              </h2>
              <div className="flex flex-wrap gap-3 text-xs sm:text-sm text-gray-600">
                <span>
                  <strong>Total încasări:</strong> {formatCurrency(archive.totalRevenue)} RON
                </span>
                <span>
                  <strong>Total comisioane:</strong> {formatCurrency(archive.totalCommission)} RON
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Încăsări generale
                </h3>
                {archive.revenues.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    Fără încasări înregistrate pentru această lună.
                  </p>
                ) : (
                  archive.revenues.map((item) => (
                    <div
                      key={item.medic.email || item.medic.name}
                      className="border border-gray-200 rounded-lg bg-white shadow-sm mb-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b">
                        <div>
                          <p className="font-semibold text-sm sm:text-base text-gray-800">
                            {item.medic.name}
                          </p>
                          <p className="text-xs text-gray-500">{item.medic.email}</p>
                        </div>
                        <p className="text-xs sm:text-sm font-semibold text-blue-700">
                          Total: {formatCurrency(item.total)} RON
                        </p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs sm:text-sm">
                          <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide text-left">
                            <tr>
                              <th className="px-3 py-2">Data</th>
                              <th className="px-3 py-2">Pacient</th>
                              <th className="px-3 py-2 text-right">Sumă încasată</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {item.entries.map((entry, index) => (
                              <tr
                                key={`${item.medic.email}-${entry.patientId}-${index}`}
                                className="text-gray-700"
                              >
                                <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                                  {formatDateValue(entry.date)}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap font-medium">
                                  {entry.patientName}
                                </td>
                                <td className="px-3 py-2 text-right font-semibold text-blue-700 whitespace-nowrap">
                                  {formatCurrency(entry.amount)} RON
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Comisioane medici
                </h3>
                {archive.commissions.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    Fără comisioane înregistrate pentru această lună.
                  </p>
                ) : (
                  archive.commissions.map((item) => (
                    <div
                      key={item.medic.email || item.medic.name}
                      className="border border-gray-200 rounded-lg bg-white shadow-sm mb-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b">
                        <div>
                          <p className="font-semibold text-sm sm:text-base text-gray-800">
                            {item.medic.name}
                          </p>
                          <p className="text-xs text-gray-500">{item.medic.email}</p>
                        </div>
                        <p className="text-xs sm:text-sm font-semibold text-blue-700">
                          Total: {formatCurrency(item.total)} RON
                        </p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs sm:text-sm">
                          <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide text-left">
                            <tr>
                              <th className="px-3 py-2">Data</th>
                              <th className="px-3 py-2">Pacient</th>
                              <th className="px-3 py-2">Procent</th>
                              <th className="px-3 py-2 text-right">Comision</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {item.entries.map((entry, index) => (
                              <tr
                                key={`${item.medic.email}-${entry.patientId}-${index}`}
                                className="text-gray-700"
                              >
                                <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                                  {formatDateValue(entry.date)}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap font-medium">
                                  {entry.patientName}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                                  {entry.category === "retratamente" ? "10%" : "20%"}
                                </td>
                                <td className="px-3 py-2 text-right font-semibold text-blue-700 whitespace-nowrap">
                                  {formatCurrency(entry.amount)} RON
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const tabs = [
    { key: "revenues", label: "Încăsări\ngenerale" },
    { key: "commissions", label: "Comisioane\nmedici" },
    { key: "chart", label: "Analiză\nlunară" },
    { key: "archive", label: "Arhivă\nlunară" },
  ];

  const renderTabs = () => (
    <div className="flex gap-2 border-b border-gray-200 pb-2 text-xs sm:text-sm">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 min-w-[72px] rounded-xl px-3 py-2 text-center font-medium leading-tight whitespace-pre-line transition ${
              isActive
                ? "bg-blue-100 text-blue-700 border border-blue-500 shadow-sm"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 border border-transparent"
            }`}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );

  const renderActiveContent = () => {
    switch (activeTab) {
      case "revenues":
        return renderRevenuesTab();
      case "commissions":
        return renderCommissionsTab();
      case "chart":
        return renderChartTab();
      case "archive":
        return renderArchiveTab();
      default:
        return renderRevenuesTab();
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <main className="flex-1 pt-24 sm:pt-28 p-4 sm:p-6 max-w-6xl mx-auto w-full">
        <section className="bg-white rounded-2xl shadow p-6 sm:p-8 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Raport lunar
              </h1>
              <p className="text-sm text-gray-500">
                Monitorizează încăsările și comisioanele generate de tratamente.
              </p>
            </div>
          </div>

          {loading ? (
            <Loader text="Se încarcă datele financiare..." />
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : monthOrder.length === 0 ? (
            <p className="text-sm text-gray-600">
              Nu există date financiare disponibile momentan.
            </p>
          ) : (
            <>
              {renderTabs()}
              <div className="pt-4">{renderActiveContent()}</div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
